/**
 * A reusable accordion component with a header and collapsible content.
 *
 * ## Attributes
 * - `header-default-text`: The text to display in the accordion header
 * - `header-dict-key`: Dictionary key for a localized header (falls back to `header-default-text` while unresolved)
 * - `header`: The old spelling of `header-default-text`. Still read, warns once per page, removed in 0.19.
 * - `open`: When present, the accordion is expanded
 * - `help-tooltip-default-text`: Default text for help tooltip
 * - `help-tooltip-dict-key`: Dictionary key for localized help tooltip
 * - `help-placement`: Placement of the help tooltip (default: "right")
 *
 * ## Slots
 * - (default): Accordion body content
 * - `help`: An `<as-hovercard>` element to use as the header help popover
 * - `header-start`: Content inserted before the header text (e.g. a leading icon or swatch)
 * - `header-end`: Content inserted after the spacer, before the arrow (e.g. a badge or count)
 *
 * ## Events
 * - `toggle`: Dispatched when the accordion is opened or closed
 *   - Detail: `{ open: boolean }`
 *
 * ## Usage
 * ```html
 * <as-accordion header-default-text="Section Title" open>
 *   <p>Content goes here</p>
 * </as-accordion>
 *
 * <!-- With leading icon and trailing badge -->
 * <as-accordion header-default-text="Files">
 *   <span slot="header-start">📁</span>
 *   <span slot="header-end">3</span>
 *   <p>Content</p>
 * </as-accordion>
 *
 * <!-- With help tooltip -->
 * <as-accordion header-default-text="Section" help-tooltip-default-text="Help text here">
 *   <p>Content</p>
 * </as-accordion>
 *
 * <!-- With hovercard -->
 * <as-accordion header-default-text="Section">
 *   <as-hovercard slot="help">Detailed help content</as-hovercard>
 *   <p>Content</p>
 * </as-accordion>
 * ```
 */
import '../overlays/as-hovercard-wrapper.ts';
import '../overlays/as-tooltip-wrapper.ts';
import '../text-and-lang/as-text.ts';
import styleText from './as-accordion.css?raw';
import flashStyle from '../../features/styles/flash.css?raw';
import { applyLocalizedText, applyLabelVars } from '../base.ts';
import { sendEv } from '../../data/events.ts';
import { warnDeprecated } from '../../util/deprecatedEl.ts';

export class AsAccordion extends HTMLElement {
  static readonly tag = 'as-accordion' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private static readonly _flashSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(flashStyle);
    return sheet;
  })();

  private _helpTooltipDefaultText: string = '';
  private _helpTooltipDictKey: string = '';
  // Placeholder values for {0}, {1}, ... in the header — set programmatically,
  // no attribute equivalent (headers are markup, vars are runtime data).
  private _headerVars: any[] | null = null;
  get headerVars(): any[] | null { return this._headerVars; }
  set headerVars(value: any[] | null) {
    this._headerVars = value;
    this._updateHeaderText();
  }

  static get observedAttributes(): string[] {
    return ['header-default-text', 'header-dict-key', 'header', 'open', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsAccordion._sheet, AsAccordion._flashSheet];
    this._render();
  }

  private _render(): void {
    this.shadowRoot!.innerHTML = /*html*/`
      <div class="accordion-container">
        <div class="accordion-header">
          <slot name="header-start"></slot>
          <as-text class="header-text"></as-text>
          <span class="header-help"></span>
          <span class="header-spacer"></span>
          <slot name="header-end"></slot>
          <span class="arrow"></span>
        </div>
        <div class="accordion-content">
          <div class="accordion-content-inner">
            <slot></slot>
          </div>
        </div>
      </div>
      <slot name="help" style="display:none;"></slot>
    `;

    this._updateHeaderText();
    this._setupEventListeners();
    this._updateHelpIcon();
  }

  private _updateHeaderText(): void {
    const headerEl = this.shadowRoot?.querySelector('.header-text') as HTMLElement | null;
    if (!headerEl) return;
    if (this.hasAttribute('header')) {
      warnDeprecated('as-accordion.header', "[DEPRECATED] as-accordion 'header' is removed in 0.19. Use 'header-default-text', which pairs with 'header-dict-key'.");
    }
    const dictKey = this.getAttribute('header-dict-key');
    applyLabelVars(headerEl, this._headerVars);
    applyLocalizedText(headerEl, dictKey, this.header);
  }

  private _isClickFromHeaderSlot(e: Event): boolean {
    const path = e.composedPath();
    for (const slotName of ['header-start', 'header-end']) {
      const slot = this.shadowRoot?.querySelector(`slot[name="${slotName}"]`) as HTMLSlotElement | null;
      if (!slot) continue;
      for (const assigned of slot.assignedElements({ flatten: true })) {
        if (path.some(node => node === assigned || (node instanceof Node && assigned.contains(node)))) {
          return true;
        }
      }
    }
    return false;
  }

  private _setupEventListeners(): void {
    const header = this.shadowRoot?.querySelector('.accordion-header');
    header?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.header-help')) return;
      if (this._isClickFromHeaderSlot(e)) return;
      this.toggle();
    });

    // Listen for slotchange on help slot
    const helpSlot = this.shadowRoot?.querySelector('slot[name="help"]') as HTMLSlotElement;
    helpSlot?.addEventListener('slotchange', () => this._updateHelpIcon());
  }

  private _updateHelpIcon(): void {
    const helpContainer = this.shadowRoot?.querySelector('.header-help') as HTMLElement;
    if (!helpContainer) return;

    const placement = this.getAttribute('help-placement') ?? 'right';

    // Check for slotted hovercard
    const helpSlot = this.shadowRoot?.querySelector('slot[name="help"]') as HTMLSlotElement;
    const slottedHovercard = helpSlot?.assignedElements().find(
      el => el.tagName.toLowerCase() === 'as-hovercard'
    ) as HTMLElement | null;

    const hasTooltip = !!(this._helpTooltipDictKey || this._helpTooltipDefaultText);
    const hasHovercard = !!slottedHovercard;

    if (hasHovercard) {
      // Clone the hovercard into the wrapper
      const hovercardClone = slottedHovercard.cloneNode(true) as HTMLElement;
      // The close button is opt-in via show-close; strip it so the hover-help
      // card never renders one. The original may already have inserted its
      // button into the light DOM, so drop the cloned (listener-less) node too.
      hovercardClone.removeAttribute('show-close');
      hovercardClone.querySelector('.as-hovercard-close')?.remove();
      hovercardClone.removeAttribute('slot');

      helpContainer.innerHTML = /*html*/`
        <as-hovercard-wrapper placement="${placement}" show-on-hover>
          <span class="help-icon">?</span>
        </as-hovercard-wrapper>
      `;

      const wrapper = helpContainer.querySelector('as-hovercard-wrapper');
      wrapper?.appendChild(hovercardClone);
    } else if (hasTooltip) {
      const attrs: string[] = [];
      if (this._helpTooltipDictKey) {
        attrs.push(`dict-key="${this._helpTooltipDictKey}"`);
      }
      if (this._helpTooltipDefaultText) {
        attrs.push(`default-text="${this._helpTooltipDefaultText}"`);
      }

      helpContainer.innerHTML = /*html*/`
        <as-tooltip-wrapper placement="${placement}" show-on-hover ${attrs.join(' ')}>
          <span class="help-icon">?</span>
        </as-tooltip-wrapper>
      `;
    } else {
      helpContainer.innerHTML = '';
    }
  }

  public toggle(): void {
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'open':
        sendEv(this, 'toggle', {
          detail: { open: this.hasAttribute('open') }
        });
        break;
      case 'header-default-text':
      case 'header':
      case 'header-dict-key':
        this._updateHeaderText();
        break;
      case 'help-tooltip-default-text':
        this._helpTooltipDefaultText = newValue ?? '';
        this._updateHelpIcon();
        break;
      case 'help-tooltip-dict-key':
        this._helpTooltipDictKey = newValue ?? '';
        this._updateHelpIcon();
        break;
      case 'help-placement':
        this._updateHelpIcon();
        break;
    }
  }

  get open(): boolean {
    return this.hasAttribute('open');
  }

  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  /** The fallback header text. header-default-text wins over the deprecated header. */
  get header(): string {
    return this.getAttribute('header-default-text') ?? this.getAttribute('header') ?? 'Section';
  }

  set header(value: string) {
    this.setAttribute('header-default-text', value);
  }
}

if (!customElements.get(AsAccordion.tag)) {
  customElements.define(AsAccordion.tag, AsAccordion);
} else {
  console.warn('as-accordion already defined');
}
